<template>
  <div class="vs-page">
    <!-- Loading state -->
    <div
      v-if="isLoading"
      class="vs-loading"
    >
      <b-spinner
        variant="primary"
        type="grow"
        label="Loading vacation details"
        class="mb-1"
      />
      <p class="vs-loading__text">
        Fetching vacation details...
      </p>
    </div>

    <!-- Content -->
    <div
      v-else
      class="vs-shell"
    >
      <!-- Hero header -->
      <header class="vs-hero">
        <div class="vs-hero__left">
          <div class="vs-hero__icon-wrap">
            <feather-icon
              icon="CalendarIcon"
              size="18"
            />
          </div>
          <div class="vs-hero__titles">
            <p class="vs-hero__eyebrow">
              Vacation request
            </p>
            <h1 class="vs-hero__title">
              {{ (leaveData.user && leaveData.user.name) || 'Vacation details' }}
            </h1>
            <p class="vs-hero__subtitle">
              <feather-icon
                icon="MapPinIcon"
                size="14"
                class="mr-25"
              />
              <span>{{ (leaveData.office && leaveData.office.name) || 'No office assigned' }}</span>
            </p>
          </div>
        </div>
        <div class="vs-hero__right">
          <span
            class="vs-status-pill"
            :class="statusPillClass(leaveData.status)"
          >
            <feather-icon
              v-if="leaveData.status === 'approved'"
              icon="CheckCircleIcon"
              size="14"
              class="mr-25"
            />
            <feather-icon
              v-else-if="leaveData.status === 'rejected'"
              icon="XCircleIcon"
              size="14"
              class="mr-25"
            />
            <feather-icon
              v-else-if="leaveData.status === 'returned'"
              icon="CornerUpLeftIcon"
              size="14"
              class="mr-25"
            />
            <feather-icon
              v-else
              icon="ClockIcon"
              size="14"
              class="mr-25"
            />
            <span class="text-capitalize">{{ leaveData.status || 'pending' }}</span>
          </span>
          <div class="vs-hero__dates">
            <div
              class="vs-hero__date-chip vs-hero__date-chip--click"
              role="button"
              tabindex="0"
              @click="openEditDatesModal('start_date')"
              @keydown.enter.prevent="openEditDatesModal('start_date')"
              @keydown.space.prevent="openEditDatesModal('start_date')"
            >
              <feather-icon
                icon="CalendarIcon"
                size="14"
                class="mr-25"
              />
              <span>{{ formatDate(leaveData.start_date) }}</span>
            </div>
            <div class="vs-hero__date-chip vs-hero__date-chip--arrow">
              <feather-icon
                icon="ArrowRightIcon"
                size="14"
                class="mr-25"
              />
            </div>
            <div
              class="vs-hero__date-chip vs-hero__date-chip--click"
              role="button"
              tabindex="0"
              @click="openEditDatesModal('end_date')"
              @keydown.enter.prevent="openEditDatesModal('end_date')"
              @keydown.space.prevent="openEditDatesModal('end_date')"
            >
              <feather-icon
                icon="CalendarIcon"
                size="14"
                class="mr-25"
              />
              <span>{{ formatDate(leaveData.end_date) }}</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Main grid -->
      <section class="vs-grid">
        <!-- Left: core details -->
        <article class="vs-card vs-card--primary">
          <h2 class="vs-card__title">
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="mr-50"
            />
            Request overview
          </h2>
          <div class="vs-details">
            <div class="vs-detail-row">
              <div class="vs-detail-label">
                <feather-icon
                  icon="UserIcon"
                  size="14"
                />
                <span>Employee</span>
              </div>
              <div class="vs-detail-value">
                <span v-if="leaveData.user">
                  {{ leaveData.user.name }}
                  <span class="vs-detail-muted">({{ leaveData.user.email }})</span>
                </span>
                <span
                  v-else
                  class="vs-detail-muted"
                >—</span>
              </div>
            </div>

            <div class="vs-detail-row">
              <div class="vs-detail-label">
                <feather-icon
                  icon="ShieldIcon"
                  size="14"
                />
                <span>Approver</span>
              </div>
              <div class="vs-detail-value">
                <span v-if="leaveData.approver">
                  {{ leaveData.approver.name }}
                  <span class="vs-detail-muted">({{ leaveData.approver.email }})</span>
                </span>
                <span
                  v-else
                  class="vs-detail-muted"
                >—</span>
              </div>
            </div>

            <div
              v-if="leaveData.office"
              class="vs-detail-row"
            >
              <div class="vs-detail-label">
                <feather-icon
                  icon="BriefcaseIcon"
                  size="14"
                />
                <span>Office</span>
              </div>
              <div class="vs-detail-value">
                {{ leaveData.office.name }}
              </div>
            </div>

            <div class="vs-detail-row">
              <div class="vs-detail-label">
                <feather-icon
                  icon="TagIcon"
                  size="14"
                />
                <span>Leave type</span>
              </div>
              <div class="vs-detail-value">
                {{ leaveData.type === 'other' ? leaveData.other_type : leaveData.type }}
              </div>
            </div>

            <div class="vs-detail-row">
              <div class="vs-detail-label">
                <feather-icon
                  icon="ClipboardIcon"
                  size="14"
                />
                <span>Description</span>
              </div>
              <div class="vs-detail-value">
                {{ leaveData.description || 'No description provided.' }}
              </div>
            </div>

            <div class="vs-detail-row">
              <div class="vs-detail-label">
                <feather-icon
                  icon="HashIcon"
                  size="14"
                />
                <span>Days count</span>
              </div>
              <div class="vs-detail-value">
                {{ leaveData.days_count || 0 }}
              </div>
            </div>
          </div>
        </article>

        <!-- Right: widgets -->
        <aside class="vs-widgets">
          <!-- Current balance -->
          <div
            class="vs-widget vs-widget--clickable"
            @click="toggleBalance"
          >
            <div class="vs-widget__icon vs-widget__icon--accent">
              <feather-icon
                icon="ActivityIcon"
                size="18"
              />
            </div>
            <div class="vs-widget__body">
              <p class="vs-widget__label">
                Current leaves balance
              </p>
              <p class="vs-widget__hint">
                Click to view detailed balance per type
              </p>
            </div>
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
              class="vs-widget__chevron"
            />
          </div>

          <!-- Attachments -->
          <div class="vs-widget">
            <div class="vs-widget__icon vs-widget__icon--muted">
              <feather-icon
                icon="PaperclipIcon"
                size="18"
              />
            </div>
            <div class="vs-widget__body vs-widget__body--column">
              <p class="vs-widget__label">
                Attachments
              </p>
              <ul class="vs-attachments">
                <li
                  v-for="file in leaveData.attachments"
                  :key="file.id"
                  class="vs-attachments__item"
                >
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="mr-25"
                  />
                  <a
                    :href="file.url"
                    target="_blank"
                  >
                    {{ file.url.split('/').pop() }}
                  </a>
                </li>
                <li
                  v-if="!leaveData.attachments || !leaveData.attachments.length"
                  class="vs-attachments__item vs-attachments__item--empty"
                >
                  No attachments
                </li>
              </ul>
            </div>
          </div>

          <!-- Office holidays -->
          <div
            v-if="leaveData.office"
            class="vs-widget vs-widget--clickable"
            @click="toggleOfficeHolidays"
          >
            <div class="vs-widget__icon vs-widget__icon--info">
              <feather-icon
                icon="CalendarIcon"
                size="18"
              />
            </div>
            <div class="vs-widget__body">
              <p class="vs-widget__label">
                Office holidays
              </p>
              <p class="vs-widget__hint">
                Weekly & yearly holidays for {{ leaveData.office.name }}
              </p>
            </div>
            <feather-icon
              icon="ChevronRightIcon"
              size="16"
              class="vs-widget__chevron"
            />
          </div>
        </aside>
      </section>

      <!-- Approvers timeline -->
      <section class="vs-approvers">
        <header class="vs-approvers__header">
          <div class="vs-approvers__title-wrap">
            <div class="vs-approvers__icon">
              <feather-icon
                icon="UsersIcon"
                size="18"
              />
            </div>
            <div>
              <h2 class="vs-approvers__title">
                Approval workflow
              </h2>
              <p class="vs-approvers__subtitle">
                All approvers with their decision and remarks
              </p>
            </div>
          </div>
        </header>

        <div
          v-if="leaveData.approvers && leaveData.approvers.length"
          class="vs-approvers__list"
        >
          <div
            v-for="(user , index) in leaveData.approvers"
            :key="index"
            class="vs-approver-card"
          >
            <div class="vs-approver-card__line" />
            <div class="vs-approver-card__dot" />
            <div class="vs-approver-card__content">
              <div class="vs-approver-card__row">
                <div class="vs-approver-card__label">
                  <feather-icon
                    icon="UserCheckIcon"
                    size="14"
                    class="mr-25"
                  />
                  <span>Approver</span>
                </div>
                <div class="vs-approver-card__value">
                  {{ user.approver.name }}
                  <span class="vs-detail-muted">({{ user.approver.email }})</span>
                </div>
              </div>

              <div class="vs-approver-card__row">
                <div class="vs-approver-card__label">
                  <feather-icon
                    icon="FlagIcon"
                    size="14"
                    class="mr-25"
                  />
                  <span>Status</span>
                </div>
                <div class="vs-approver-card__value">
                  <span :class="statusClass(user.status)">
                    {{ user.status }}
                  </span>
                </div>
              </div>

              <div
                v-if="user.decision_at"
                class="vs-approver-card__row"
              >
                <div class="vs-approver-card__label">
                  <feather-icon
                    icon="ClockIcon"
                    size="14"
                    class="mr-25"
                  />
                  <span>Decision at</span>
                </div>
                <div class="vs-approver-card__value">
                  {{ formatDate(user.decision_at) }}
                </div>
              </div>

              <div class="vs-approver-card__row">
                <div class="vs-approver-card__label">
                  <feather-icon
                    icon="MessageCircleIcon"
                    size="14"
                    class="mr-25"
                  />
                  <span>Remark</span>
                </div>
                <div class="vs-approver-card__value">
                  {{ user.remark || '—' }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <p
          v-else
          class="vs-approvers__empty"
        >
          No approvers found for this request.
        </p>
      </section>

      <!-- Decision CTA -->
      <footer class="vs-footer">
        <b-button
          v-if="leaveData.status !== 'returned'"
          variant="primary"
          class="vs-footer__btn"
          @click="toggleStatusModal"
        >
          <feather-icon
            icon="Edit3Icon"
            size="16"
            class="mr-50"
          />
          Choose decision
        </b-button>
      </footer>

      <!-- Balance modal -->
      <b-modal
        v-model="showBalance"
        hide-footer
        title="Current leaves balance"
        modal-class="vs-modal"
      >
        <ul class="vs-balance-list">
          <li
            v-for="bal in leaveData.current_leaves_balance"
            :key="bal.type"
            class="vs-balance-list__item"
          >
            <div class="vs-balance-list__type">
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="mr-25"
              />
              <span>{{ bal.type }}</span>
            </div>
            <span class="vs-balance-list__value">{{ bal.balance }}</span>
          </li>
          <li
            v-if="!leaveData.current_leaves_balance || !leaveData.current_leaves_balance.length"
            class="vs-balance-list__item vs-balance-list__item--empty"
          >
            No balance information available.
          </li>
        </ul>
      </b-modal>

      <!-- Office holidays modal -->
      <b-modal
        v-if="leaveData.office"
        v-model="officeHolidays"
        hide-footer
        :title="`Office holidays — ${leaveData.office.name}`"
        modal-class="vs-modal"
      >
        <div class="vs-modal-section">
          <h5 class="vs-modal-section__title">
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
              class="mr-25"
            />
            Weekly holidays
          </h5>
          <ul class="vs-modal-list">
            <li
              v-for="(day, index) in leaveData.office.weekly_holiday"
              :key="index"
            >
              {{ day }}
            </li>
          </ul>
        </div>

        <div class="vs-modal-section">
          <h5 class="vs-modal-section__title">
            <feather-icon
              icon="StarIcon"
              size="14"
              class="mr-25"
            />
            Yearly holidays
          </h5>
          <ul class="vs-modal-list">
            <li
              v-for="holiday in leaveData.office.yearly_holidays"
              :key="holiday.date"
            >
              {{ holiday.date }} — {{ holiday.name }}
            </li>
          </ul>
        </div>
      </b-modal>

      <!-- Decision modal -->
      <b-modal
        v-model="openStatusModal"
        hide-footer
        centered
        title="Choose your decision"
        modal-class="vs-modal"
      >
        <div class="vs-decision">
          <label
            class="vs-decision__label"
            for="remark"
          >
            <feather-icon
              icon="MessageCircleIcon"
              size="14"
              class="mr-25"
            />
            Add a remark (optional)
          </label>
          <b-form-input
            id="remark"
            v-model="vacationForm.remark"
            trim
            placeholder="Write a short note for the requester"
          />

          <div class="vs-decision__actions">
            <b-button
              v-if="!loadingApproved"
              variant="success"
              class="vs-decision__btn"
              :disabled="loadingApproved || loadingRejected || loadingReturned"
              @click="updateVacation('approved')"
            >
              <feather-icon
                icon="CheckCircleIcon"
                size="16"
                class="mr-50"
              />
              Approve
            </b-button>
            <b-button
              v-else
              variant="success"
              disabled
              class="vs-decision__btn"
            >
              <b-spinner
                small
                type="grow"
                class="mr-50"
              />
              Approving...
            </b-button>

            <b-button
              v-if="!loadingRejected"
              variant="danger"
              class="vs-decision__btn"
              :disabled="loadingApproved || loadingRejected || loadingReturned"
              @click="updateVacation('rejected')"
            >
              <feather-icon
                icon="XCircleIcon"
                size="16"
                class="mr-50"
              />
              Reject
            </b-button>
            <b-button
              v-else
              variant="danger"
              disabled
              class="vs-decision__btn"
            >
              <b-spinner
                small
                type="grow"
                class="mr-50"
              />
              Rejecting...
            </b-button>

            <b-button
              v-if="!loadingReturned"
              variant="primary"
              class="vs-decision__btn"
              :disabled="loadingApproved || loadingRejected || loadingReturned"
              @click="updateVacation('returned')"
            >
              <feather-icon
                icon="CornerUpLeftIcon"
                size="16"
                class="mr-50"
              />
              Return
            </b-button>
            <b-button
              v-else
              variant="primary"
              disabled
              class="vs-decision__btn"
            >
              <b-spinner
                small
                type="grow"
                class="mr-50"
              />
              Returning...
            </b-button>
          </div>
        </div>
      </b-modal>

      <!-- Edit dates modal -->
      <b-modal
        v-model="openEditDates"
        centered
        hide-footer
        title="Edit vacation dates"
        modal-class="vs-modal"
      >
        <div class="vs-modal-section">
          <h5 class="vs-modal-section__title">
            <feather-icon
              icon="CalendarIcon"
              size="14"
              class="mr-25"
            />
            Update start & end dates
          </h5>

          <div class="vs-date-form">
            <div class="vs-date-form__field">
              <label class="vs-date-form__label">Start date</label>
              <b-form-datepicker
                v-model="editDatesForm.start_date"
                class="vs-date-form__input"
                menu-class="vs-datepicker-dropdown"
                today-button
                reset-button
                close-button
                locale="en"
              />
            </div>

            <div class="vs-date-form__field">
              <label class="vs-date-form__label">End date</label>
              <b-form-datepicker
                v-model="editDatesForm.end_date"
                class="vs-date-form__input"
                menu-class="vs-datepicker-dropdown"
                today-button
                reset-button
                close-button
                locale="en"
              />
            </div>
          </div>

          <p
            v-if="editDatesError"
            class="text-danger small mb-50"
          >
            {{ editDatesError }}
          </p>

          <div class="vs-modal-actions">
            <b-button
              variant="outline-secondary"
              :disabled="loadingEditDates"
              @click="openEditDates = false"
            >
              Cancel
            </b-button>
            <b-button
              v-if="!loadingEditDates"
              variant="primary"
              @click="saveEditDates"
            >
              Save
            </b-button>
            <b-button
              v-else
              variant="primary"
              disabled
            >
              <b-spinner
                small
                type="grow"
                class="mr-50"
              />
              Saving...
            </b-button>
          </div>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'
import formatDate from '@/global-compositions/formatDate'

export default {
  data() {
    return {
      showBalance: false,
      officeHolidays: false,
      openStatusModal: false,
      openEditDates: false,
      isLoading: false,
      leaveData: {},
      vacationForm: {},
      loadingApproved: false,
      loadingRejected: false,
      loadingReturned: false,
      loadingEditDates: false,
      editDatesForm: {
        start_date: '',
        end_date: '',
      },
      editDatesError: '',
    }
  },
  mounted() {
    this.getVacation()
  },
  methods: {
    toggleBalance() {
      this.showBalance = !this.showBalance
    },
    toggleStatusModal() {
      this.openStatusModal = !this.openStatusModal
    },
    toggleOfficeHolidays() {
      this.officeHolidays = !this.officeHolidays
    },
    formatDate(date) {
      return formatDate(date)
    },
    statusClass(status) {
      const base = 'vs-status'
      if (status === 'approved') return [base, 'vs-status--approved']
      if (status === 'rejected') return [base, 'vs-status--rejected']
      if (status === 'returned') return [base, 'vs-status--returned']
      return [base, 'vs-status--pending']
    },
    statusPillClass(status) {
      if (status === 'approved') return 'vs-status-pill--approved'
      if (status === 'rejected') return 'vs-status-pill--rejected'
      if (status === 'returned') return 'vs-status-pill--returned'
      return 'vs-status-pill--pending'
    },
    async getVacation() {
      this.isLoading = true
      try {
        const res = await axios.get(
          `https://gulftic-system.fci.group/api/vacation/${this.$route.params.id}`,
        )
        this.leaveData = decryptData(res.data.payload).data.vacation
      } catch (error) {
        if (error.response?.status) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: `${error.response.data.message}`,
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        }
      } finally {
        this.isLoading = false
      }
    },
    async updateVacation(form) {
      try {
        // Set loading state depending on the chosen status
        if (form === 'approved') {
          this.loadingApproved = true
        } else if (form === 'rejected') {
          this.loadingRejected = true
        } else {
          this.loadingReturned = true
        }

        // Update form status
        this.vacationForm.status = form

        // Send request
        const res = await axios.patch(
          `/vacation/${this.$route.params.id}/change-status`,
          this.vacationForm,
        )

        // Success handling
        if (res.status === 200) {
          this.$toasted.show('Updated Successfully', {
            position: 'top-center',
            duration: 3000,
          })
        }

        this.getVacation()
        this.toggleStatusModal()
      } catch (error) {
        if (error.response.status) {
          const payload = decryptData(error.response.data.payload).message
          const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
          const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : payload || 'An unknown error occurred'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: 'BellIcon',
              variant: 'error',
            },
          })
        }
      } finally {
        // Always reset all loading states
        this.loadingApproved = false
        this.loadingRejected = false
        this.loadingReturned = false
      }
    },

    openEditDatesModal(focusField) {
      this.editDatesError = ''
      this.editDatesForm = {
        start_date: this.leaveData.start_date || '',
        end_date: this.leaveData.end_date || '',
      }
      this.openEditDates = true

      this.$nextTick(() => {
        try {
          const el = document.querySelector(
            focusField === 'start_date'
              ? '.vs-date-form__field:first-child input'
              : '.vs-date-form__field:last-child input',
          )
          if (el && typeof el.focus === 'function') el.focus()
        } catch (e) {
          // ignore
        }
      })
    },

    async saveEditDates() {
      this.editDatesError = ''

      if (!this.editDatesForm.start_date || !this.editDatesForm.end_date) {
        this.editDatesError = 'Start date and end date are required.'
        return
      }

      if (String(this.editDatesForm.end_date) < String(this.editDatesForm.start_date)) {
        this.editDatesError = 'End date must be after start date.'
        return
      }

      this.loadingEditDates = true
      try {
        const payload = {
          start_date: this.editDatesForm.start_date,
          end_date: this.editDatesForm.end_date,
          type: this.leaveData.type,
          other_type: this.leaveData.other_type,
          description: this.leaveData.description,
          user_id: this.leaveData.user?.id,
          office_id: this.leaveData.office?.id,
        }

        Object.keys(payload).forEach(k => {
          if (payload[k] === undefined) delete payload[k]
        })

        const res = await axios.put(
          `/vacation/${this.$route.params.id}`,
          payload,
        )

        if (res.status === 200) {
          this.$toasted.show('Dates updated successfully', {
            position: 'top-center',
            duration: 3000,
          })
        }

        this.openEditDates = false
        await this.getVacation()
      } catch (error) {
        if (error.response?.status) {
          const payload = decryptData(error.response.data.payload).message
          const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
          const errorMessage = firstArrayKey ? payload[firstArrayKey][0] : payload || 'An unknown error occurred'
          this.$toast({
            component: ToastificationContent,
            props: {
              title: errorMessage,
              icon: 'BellIcon',
              variant: 'error',
            },
          })
          return
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Network error while saving dates',
            icon: 'BellIcon',
            variant: 'error',
          },
        })
      } finally {
        this.loadingEditDates = false
      }
    },

  },
}
</script>

<style scoped lang="scss">
$violet:  #7c3aed;
$violet2: #5b21b6;
$indigo:  #4f46e5;
$cyan:    #06b6d4;
$success: #22c55e;
$danger:  #ef4444;
$amber:   #f59e0b;
$rose:    #fb7185;
$warning: #f97316;
$border:  #e2e8f0;
$bg-page: #f1f5f9;
$text:    #0f172a;
$muted:   #64748b;
$white:   #ffffff;

.vs-page {
  min-height: 100vh;
  padding: 1.75rem 1.5rem;
  background: radial-gradient(circle at top left, rgba($violet, 0.09), transparent 55%),
    radial-gradient(circle at bottom right, rgba($cyan, 0.12), transparent 55%),
    $bg-page;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  box-sizing: border-box;
}

.vs-shell {
  width: 100%;
  max-width: 1100px;
  background: $white;
  border-radius: 1.1rem;
  box-shadow:
    0 24px 60px rgba($text, 0.13),
    0 6px 18px rgba($text, 0.06);
  padding: 1.4rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
}

.vs-loading {
  width: 100%;
  max-width: 480px;
  margin: 4rem auto 0;
  text-align: center;

  &__text {
    margin-top: 0.75rem;
    font-size: 0.86rem;
    color: $muted;
  }
}

/* Hero */
.vs-hero {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.95rem 1rem;
  border-radius: 0.9rem;
  background: linear-gradient(120deg, rgba($violet2, 0.97), rgba($indigo, 0.95));
  color: $white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at top right, rgba($white, 0.08), transparent 60%);
    opacity: 0.9;
    pointer-events: none;
  }
}

.vs-hero__left,
.vs-hero__right {
  position: relative;
  z-index: 1;
}

.vs-hero__left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.vs-hero__icon-wrap {
  width: 40px;
  height: 40px;
  border-radius: 1rem;
  background: rgba($white, 0.14);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 24px rgba($text, 0.35);
}

.vs-hero__eyebrow {
  font-size: 0.64rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  opacity: 0.82;
  margin-bottom: 0.2rem;
}

.vs-hero__title {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: -0.03em;
  margin: 0 0 0.1rem;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  color: #fefce8; // soft warm light instead of pure white
}

.vs-hero__subtitle {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.78rem;
  opacity: 0.9;
}

.vs-hero__right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.45rem;
}

.vs-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  background: rgba($white, 0.16);
  backdrop-filter: blur(6px);
  color: #fefce8;
}

.vs-status-pill--approved {
  background: linear-gradient(120deg, rgba($success, 0.2), rgba($cyan, 0.22));
  color: #ecfdf3;
}

.vs-status-pill--rejected {
  background: linear-gradient(120deg, rgba($danger, 0.24), rgba($rose, 0.26));
  color: #fee2e2;
}

.vs-status-pill--pending {
  background: linear-gradient(120deg, rgba($warning, 0.26), rgba($amber, 0.24));
  color: #fffbeb;
}

.vs-status-pill--returned {
  background: linear-gradient(120deg, rgba($cyan, 0.28), rgba($indigo, 0.24));
  color: #e0f2fe;
}

.vs-hero__dates {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.vs-hero__date-chip {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.5rem;
  border-radius: 999px;
  font-size: 0.76rem;
  background: rgba($white, 0.12);

  &--click {
    cursor: pointer;
    transition: transform 0.12s ease, background 0.12s ease;

    &:hover {
      background: rgba($white, 0.18);
      transform: translateY(-1px);
    }

    &:focus {
      outline: 2px solid rgba($white, 0.45);
      outline-offset: 2px;
    }
  }

  &--arrow {
    padding-inline: 0.25rem;
  }
}

/* Grid */
.vs-grid {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 1rem;
}

.vs-card {
  background: #f9fafb;
  border-radius: 0.75rem;
  padding: 0.85rem 0.9rem;

  &--primary {
    background: linear-gradient(135deg, rgba($violet, 0.03), rgba($cyan, 0.04));
    border: 1px solid rgba($violet, 0.16);
  }
}

.vs-card__title {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: $violet2;
  margin: 0 0 0.55rem;
}

.vs-details {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.84rem;
}

.vs-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
}

.vs-detail-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: $muted;
}

.vs-detail-value {
  text-align: right;
  font-weight: 500;
  color: $text;
}

.vs-detail-muted {
  color: $muted;
  font-weight: 400;
  margin-left: 0.2rem;
  font-size: 0.78rem;
}

/* Widgets */
.vs-widgets {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.vs-widget {
  display: flex;
  align-items: center;
  padding: 0.65rem 0.7rem;
  border-radius: 0.7rem;
  background: #f9fafb;
  border: 1px solid rgba($border, 0.9);
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.08s ease;

  &--clickable {
    cursor: pointer;

    &:hover {
      background: #eef2ff;
      box-shadow: 0 12px 26px rgba($violet, 0.18);
      transform: translateY(-1px);
    }
  }
}

.vs-widget__icon {
  width: 32px;
  height: 32px;
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.55rem;

  &--accent {
    background: rgba($violet, 0.12);
    color: $violet2;
  }

  &--muted {
    background: rgba($muted, 0.08);
    color: $muted;
  }

  &--info {
    background: rgba($cyan, 0.14);
    color: $indigo;
  }
}

.vs-widget__body {
  flex: 1;
  min-width: 0;

  &--column {
    display: flex;
    flex-direction: column;
  }
}

.vs-widget__label {
  font-size: 0.84rem;
  font-weight: 600;
  color: $text;
  margin-bottom: 0.15rem;
}

.vs-widget__hint {
  font-size: 0.78rem;
  color: $muted;
}

.vs-widget__chevron {
  color: $muted;
  margin-left: 0.4rem;
}

.vs-attachments {
  list-style: none;
  padding: 0;
  margin: 0.1rem 0 0;

  &__item {
    display: flex;
    align-items: center;
    font-size: 0.8rem;
    color: $text;

    & + & {
      margin-top: 0.2rem;
    }

    a {
      color: $violet2;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    &--empty {
      color: $muted;
      font-style: italic;
    }
  }
}

/* Approvers timeline */
.vs-approvers {
  margin-top: 0.4rem;
}

.vs-approvers__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.6rem;
}

.vs-approvers__title-wrap {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.vs-approvers__icon {
  width: 34px;
  height: 34px;
  border-radius: 0.7rem;
  background: rgba($indigo, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: $indigo;
}

.vs-approvers__title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: $text;
}

.vs-approvers__subtitle {
  margin: 0.05rem 0 0;
  font-size: 0.8rem;
  color: $muted;
}

.vs-approvers__list {
  position: relative;
  margin-top: 0.3rem;
}

.vs-approver-card {
  position: relative;
  padding-left: 1.5rem;
  padding-bottom: 0.8rem;

  &__line {
    position: absolute;
    left: 0.6rem;
    top: 0;
    bottom: 0.2rem;
    width: 2px;
    background: linear-gradient(to bottom, rgba($violet, 0.4), transparent);
  }

  &__dot {
    position: absolute;
    left: 0.45rem;
    top: 0.5rem;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: $violet;
    box-shadow: 0 0 0 4px rgba($violet, 0.25);
  }

  &__content {
    background: #f9fafb;
    border-radius: 0.7rem;
    padding: 0.55rem 0.7rem;
    border: 1px solid rgba($border, 0.9);
  }

  &__row {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    font-size: 0.8rem;

    & + & {
      margin-top: 0.25rem;
    }
  }

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: $muted;
    font-weight: 600;
  }

  &__value {
    text-align: right;
    color: $text;
    font-weight: 500;
  }
}

.vs-approvers__empty {
  margin: 0.4rem 0 0;
  font-size: 0.82rem;
  color: $muted;
  font-style: italic;
}

/* Footer */
.vs-footer {
  margin-top: 0.4rem;
  display: flex;
  justify-content: flex-end;
}

.vs-footer__btn {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  border-radius: 999px;
  padding-inline: 1.1rem;
}

/* Status helper */
.vs-status {
  display: inline-flex;
  align-items: center;
  padding: 0.1rem 0.6rem;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: capitalize;

  &--approved {
    background: rgba($success, 0.14);
    color: darken($success, 10%);
  }

  &--rejected {
    background: rgba($danger, 0.14);
    color: darken($danger, 5%);
  }

  &--pending {
    background: rgba($warning, 0.14);
    color: darken($warning, 10%);
  }

  &--returned {
    background: rgba($cyan, 0.16);
    color: $indigo;
  }
}

/* Modals */
.vs-modal .modal-content {
  border-radius: 0.9rem;
  border: 1px solid $border;
}

.vs-modal .modal-header {
  border-bottom: 1px solid $border;
}

.vs-modal .modal-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: $text;
}

.vs-modal-section {
  & + & {
    margin-top: 0.75rem;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.86rem;
    font-weight: 600;
    color: $text;
    margin-bottom: 0.35rem;
  }
}

.vs-modal-list {
  padding-left: 1rem;
  margin: 0;
  font-size: 0.8rem;
  color: $muted;
}

.vs-date-form {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 0.75rem;
  margin-top: 0.35rem;

  &__field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__label {
    font-size: 0.78rem;
    color: $muted;
    font-weight: 600;
    margin: 0;
  }

  &__input {
    width: 100%;
  }
}

.vs-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.75rem;
}

/* datepicker dropdown is portaled to body */
:global(.vs-datepicker-dropdown) {
  z-index: 9999 !important;
}

.vs-balance-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 0.82rem;

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.35rem 0.1rem;

    & + & {
      border-top: 1px dashed rgba($border, 0.8);
    }

    &--empty {
      color: $muted;
      font-style: italic;
      justify-content: center;
    }
  }

  &__type {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    color: $text;
    font-weight: 500;
  }

  &__value {
    font-weight: 700;
    color: $violet2;
  }
}

.vs-decision {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  &__label {
    font-size: 0.82rem;
    color: $muted;
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    margin-bottom: 0.15rem;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.35rem;
  }

  &__btn {
    flex: 1 1 30%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
    border-radius: 999px;
  }
}

/* Responsive */
@media (max-width: 991.98px) {
  .vs-page {
    padding: 1rem 0.8rem;
  }

  .vs-shell {
    padding: 1rem 0.85rem 1.1rem;
  }

  .vs-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .vs-hero {
    flex-direction: column;
    align-items: flex-start;
  }

  .vs-hero__right {
    align-items: flex-start;
  }

  .vs-footer {
    justify-content: stretch;

    &__btn {
      width: 100%;
      justify-content: center;
    }
  }
}

@media (max-width: 575.98px) {
  .vs-page {
    padding: 0.75rem 0.5rem;
  }

  .vs-hero__title {
    font-size: 1.2rem;
  }

  .vs-shell {
    border-radius: 0.9rem;
  }
}
</style>
